/**
 * Profile media: YouTube links.
 *
 * A YouTube gallery item is stored as its 11-character video id and nothing
 * else, and the embed URL is always built from that id. A stored value can then
 * never point an iframe at anything but YouTube's own player.
 */

const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;

const YOUTUBE_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'music.youtube.com',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
]);

/** Paths that carry the id as their second segment: /shorts/<id>, /embed/<id>, … */
const ID_PATHS = new Set(['shorts', 'embed', 'live', 'v']);

export function isYouTubeId(value) {
  return typeof value === 'string' && YOUTUBE_ID.test(value);
}

/**
 * The video id from any of the links YouTube hands out (watch, youtu.be, shorts,
 * embed, live), or null. Channel, playlist and search links are not a video and
 * also come back null.
 */
export function youtubeVideoId(input) {
  if (typeof input !== 'string') return null;
  let url;
  try {
    url = new URL(input.trim());
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;

  const host = url.hostname.toLowerCase();
  const [first, second] = url.pathname.split('/').filter(Boolean);
  let id = null;
  if (host === 'youtu.be' || host === 'www.youtu.be') id = first;
  else if (YOUTUBE_HOSTS.has(host)) {
    if (first === 'watch') id = url.searchParams.get('v');
    else if (ID_PATHS.has(first)) id = second;
  }
  return isYouTubeId(id) ? id : null;
}

/** The canonical watch link for an id, which is what the API hands back. */
export const youtubeWatchUrl = (id) => `https://www.youtube.com/watch?v=${id}`;
